import {
  type APIError,
  type Execute,
  type GetQuoteParameters,
  isAPIError,
} from '@relayprotocol/relay-sdk'
import { type Address, encodeFunctionData } from 'viem'
import { getRelayClient } from './client'
import {
  FUN_RELAY_REFERRER,
  FUN_RELAY_REVENUE_WALLET,
  RELAY_QUOTE_CHECKOUT_TIME_BUFFER_MS,
} from './constants'
import { getRelayAssetInfo } from './currency'
import type {
  RelayAddress,
  RelayChainId,
  RelayVmType,
  RelayWallet,
} from './execution'
import { type FunRelayFeeItem, getFunRelayFees, parseRelayFees } from './fees'
import { getRelayAssetPriceInfo } from './price'
import type { SolanaAddress } from './solana'
import type {
  ApiFunkitCheckoutActionParams,
  CheckoutQuoteResponse,
  Logger,
} from './types'
import {
  convertFunToRelayChainId,
  convertFunToRelayTokenAddress,
} from './utils'

type RelayQuoteOptions = GetQuoteParameters['options'] & {
  // TODO: Relay does not yet explicitly handle this field in their SDK, nor is it in their typings - but it gets forwarded to API
  depositFeePayer?: SolanaAddress
}

export type RelayQuoteResult<VmType extends RelayVmType> = {
  details?: {
    currencyIn?: NonNullable<Execute['details']>['currencyIn']
    currencyOut?: NonNullable<Execute['details']>['currencyOut']
  }
  steps: Execute['steps']
  vmType: VmType
}

export type RelayExecute = Execute

export type GetRelayQuoteParams<
  SourceVmType extends RelayVmType = RelayVmType,
  TargetVmType extends RelayVmType = RelayVmType,
> = {
  actionParams?: ApiFunkitCheckoutActionParams[]
  clientId: string
  fromChainId: RelayChainId<SourceVmType>
  fromTokenAddress: RelayAddress<SourceVmType>
  logger: Logger
  /**
   * {@link getRelayQuote} already sets {@link RelayQuoteOptions.appFees|appFees} and {@link RelayQuoteOptions.referrer|referrer}.
   * Only include them if you wish to override.
   */
  options?: RelayQuoteOptions
  recipientAddress: RelayAddress<TargetVmType> | undefined
  toChainId: RelayChainId<TargetVmType>
  toTokenAddress: RelayAddress<TargetVmType>
  tradeType: 'EXACT_INPUT' | 'EXACT_OUTPUT' | 'EXPECTED_OUTPUT'
  userAddress: RelayAddress<SourceVmType> | undefined
  walletClient?: RelayWallet<SourceVmType>
  apiKey: string
  /**
   * Optional explicit user preference for the source-side authorization
   * step ("Sign message" vs "Approve transaction"). Forwarded to frog's
   * `/api/fee` so frog's `getUsePermit` can honor the FE toggle's choice.
   * Only meaningful for FE-driven flows (Wallet checkout); server-driven
   * flows (UDA, etc.) should omit this and let frog's auto-gasless
   * fallback decide.
   */
  usePermit?: boolean
} & (
  | {
      fromTokenAmountBaseUnit: bigint | string
      tradeType: 'EXACT_INPUT'
    }
  | {
      toTokenAmountBaseUnit: bigint | string
      tradeType: 'EXACT_OUTPUT' | 'EXPECTED_OUTPUT'
    }
)

export type RelayQuote<VmType extends RelayVmType = RelayVmType> =
  CheckoutQuoteResponse & {
    /** Required for EXACT_INPUT checkouts */
    finalToAmountBaseUnit: string
    metadata: {
      fromAmountBaseUnit: string
      relayQuote: RelayQuoteResult<VmType>
      toAmountBaseUnit: string
    }
  }

// this quote type is only used by backend, not exposed to FE
// and can contain additional fields that are not in the FE-facing quote type.
export type RelayBackendQuote<VmType extends RelayVmType = RelayVmType> =
  CheckoutQuoteResponse & {
    /** Required for EXACT_INPUT checkouts */
    finalToAmountBaseUnit: string
    metadata: {
      fromAmountBaseUnit: string
      relayQuote: Execute & { vmType: VmType }
      toAmountBaseUnit: string
    }
  }

async function getQuoteEstUsdValue({
  tokenChainId,
  tokenAddress,
  tokenAmountBaseUnit,
  logger,
  apiKey,
}: {
  tokenChainId: string
  tokenAddress: string
  tokenAmountBaseUnit: string
  logger: Logger
  apiKey: string
}) {
  try {
    const normalizedChainId = convertFunToRelayChainId(
      Number(tokenChainId),
    ).toString()
    const normalizedTokenAddress = convertFunToRelayTokenAddress(
      tokenAddress as Address,
      Number(tokenChainId),
    )

    const [tokenPriceRes, tokenInfoRes] = await Promise.all([
      getRelayAssetPriceInfo({
        chainId: normalizedChainId,
        address: normalizedTokenAddress,
        apiKey,
      }),
      getRelayAssetInfo({
        chainId: normalizedChainId,
        address: normalizedTokenAddress,
        apiKey,
      }),
    ])

    // FIXME: Temp hardcode until relay supports WUSDE on Ethereal
    const isWUsdeEthereal =
      normalizedChainId === '5064014' &&
      normalizedTokenAddress.toLowerCase() ===
        '0xB6fC4B1BFF391e5F6b4a3D2C7Bda1FeE3524692D'.toLowerCase()

    const price = tokenPriceRes.price
    const decimals = isWUsdeEthereal ? 18 : tokenInfoRes.decimals

    const absoluteAmount = BigInt(tokenAmountBaseUnit) / BigInt(10 ** decimals)
    return Number(price) * Number(absoluteAmount)
  } catch (err) {
    logger.error('getQuoteEstUsdValueError', {
      message: `Failed to get USD value for token ${tokenAddress} on chain ${tokenChainId}: ${getErrorMessage(
        err,
      )}. Falling back to 0 USD.`,
    })
    // If any part fails, just fallback to 0 USD so that we do not block quote
    return 0
  }
}

export function getReferrer(clientId: string) {
  return clientId ? `${FUN_RELAY_REFERRER}|${clientId}` : FUN_RELAY_REFERRER
}

export class RelayQuoteClientError extends Error {
  public constructor(
    public readonly statusCode: number,
    public readonly relayErrorCode: string,
    message: string,
    requestId: string | undefined = '',
  ) {
    super(
      `An error occurred trying to generate a relay quote: ${relayErrorCode} - ReqId ${requestId || 'NO_REQ_ID'} - ${message}`,
    )

    this.name = new.target.name
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

/**
 * Get the headers for the quote request by the following logic:
 * - If subsidizeFeesSecretKey is provided, and subsidizeFees is true, set it to "x-api-key" header
 * - Apply funRelayConfig.headers as overrides if provided
 */
export const getHeaders = (funRelayConfig: FunRelayFeeItem, apiKey: string) => {
  const headers = {
    'x-api-key': apiKey,
    // Remote headers will override passed in headers
    ...(funRelayConfig.headers ? { ...funRelayConfig.headers } : {}),
  }
  return headers
}

export async function getRelayQuote<
  SourceVmType extends RelayVmType,
  TargetVmType extends RelayVmType = RelayVmType,
>({
  logger,
  walletClient,
  ...params
}: GetRelayQuoteParams<SourceVmType, TargetVmType>): Promise<
  RelayBackendQuote<SourceVmType>
> {
  const {
    actionParams,
    clientId,
    fromChainId,
    fromTokenAddress,
    options,
    recipientAddress,
    toChainId,
    toTokenAddress,
    tradeType,
    userAddress,
    apiKey,
    usePermit,
  } = params

  const vmType = (
    walletClient && 'vmType' in walletClient ? walletClient.vmType : 'evm'
  ) as SourceVmType

  try {
    const relayClient = getRelayClient()

    // Same chain
    const isSameChainFlow = String(fromChainId) === String(toChainId)

    const txs = actionParams?.map((action) => {
      const data =
        action.rawCalldata ??
        encodeFunctionData({
          abi: action.contractAbi,
          args: action.functionArgs,
          functionName: action.functionName,
        })

      return {
        data,
        to: action.contractAddress,
        // Relay supports msg.value via 2 fields: `value` and `originalTxValue`
        // Both `value` and `originalTxValue` are required to be set for `erc20` + gas transfers
        // Only `value` is required for native token transfers. // TODO: This flow needs verification.
        // Internally for Relay,
        // - `originalTxValue` is designed to "bubble up" the value to the original tx. This only works for user-executed same-chain operations with calls. This does not work / apply to cross chain operations
        // - `value` is the internal multicall value
        ...(isSameChainFlow
          ? { originalTxValue: String(action.value ?? 0n) }
          : {}),
        value: String(action.value ?? 0n),
      }
    })

    const isExactIn = params.tradeType === 'EXACT_INPUT'
    const queryTokenAmountBaseUnit = isExactIn
      ? params.fromTokenAmountBaseUnit
      : params.toTokenAmountBaseUnit
    const queryTokenAddress = isExactIn ? fromTokenAddress : toTokenAddress
    const queryTokenChainId = isExactIn ? fromChainId : toChainId

    const estUsdValue = await getQuoteEstUsdValue({
      tokenChainId: queryTokenChainId,
      tokenAddress: queryTokenAddress,
      tokenAmountBaseUnit: queryTokenAmountBaseUnit.toString(),
      logger,
      apiKey,
    })

    const funRelayConfig = await getFunRelayFees({
      clientId,
      estUsdValue: Number(estUsdValue) || 0,
      fromTokenAddress,
      fromChainId,
      recipientAddress,
      senderAddress: userAddress,
      toTokenAddress,
      toChainId,
      usePermit,
    })

    const headers = getHeaders(funRelayConfig, apiKey)

    const appFeeBp = funRelayConfig.b + funRelayConfig.v

    logger.info('getRelayQuoteParams', {
      ...params,
      appFeeBp,
      estUsdValue,
      funRelayConfig,
      queryTokenAmountBaseUnit,
      txs,
    })

    // Get the full quote with fees
    const relayQuote = await relayClient.actions.getQuote(
      {
        amount: queryTokenAmountBaseUnit.toString(),
        chainId: convertFunToRelayChainId(Number(fromChainId)),
        currency: convertFunToRelayTokenAddress(
          fromTokenAddress,
          Number(fromChainId),
        ),
        recipient: recipientAddress,
        toChainId: convertFunToRelayChainId(Number(toChainId)),
        toCurrency: convertFunToRelayTokenAddress(
          toTokenAddress,
          Number(toChainId),
        ),
        tradeType,
        txs,
        user: userAddress,
        wallet: walletClient,
        options: {
          referrer: getReferrer(clientId),
          appFees: [
            {
              fee: appFeeBp.toString(),
              recipient: FUN_RELAY_REVENUE_WALLET,
            },
          ],
          // Passed in options
          ...options,
          // Remote options will always override passed in options
          ...funRelayConfig.config.options,
        },
      },
      undefined,
      headers,
    )

    logger.info('relayQuote', relayQuote)

    const toAmountUsd = Number(relayQuote.details?.currencyOut?.amountUsd || 0)
    const fromAmountUsd = Number(relayQuote.details?.currencyIn?.amountUsd || 0)
    // Get how much fromAmount is needed for the quote
    const fromAmountString =
      relayQuote.details?.currencyIn?.amountFormatted || '0'
    const fromAmountBaseUnitString =
      relayQuote.details?.currencyIn?.amount || '0'
    const fromAssetAmount = Number(fromAmountString || 0)

    // RequestID will be consistent across steps in the quote
    // https://fun-xyz.slack.com/archives/C08MQ85QB2N/p1747774944603209
    const relayRequestId = relayQuote.steps[0]?.requestId

    // TODO: Verify with relay team if this is possible?
    if (!relayRequestId) {
      throw new Error('Relay quote does not contain a requestId')
    }

    // TODO: Verify with relay team if this is possible?
    if (!relayQuote.details?.currencyIn || !relayQuote.details?.currencyOut) {
      throw new Error('Relay quote does not contain trade details')
    }

    const {
      relayGasFeesUsd,
      totalLpFeesUsd,
      totalFeesUsd,
      totalFeesFromAmount,
      totalFeesFromAmountBaseUnit,
    } = parseRelayFees({
      fees: relayQuote.fees,
      fromChainIdRelay: convertFunToRelayChainId(Number(fromChainId)),
      fromTokenAddressRelay: convertFunToRelayTokenAddress(
        fromTokenAddress,
        Number(fromChainId),
      ),
      logger,
      sourceAssetDecimals:
        relayQuote.details.currencyIn.currency?.decimals ?? 18,
      sourceUnitPrice: fromAmountUsd / fromAssetAmount || 1,
    })

    const estSubtotalFromAmount = (
      fromAssetAmount - totalFeesFromAmount
    ).toString()

    const estSubtotalFromAmountBaseUnit = (
      BigInt(fromAmountBaseUnitString) - totalFeesFromAmountBaseUnit
    ).toString()

    return {
      quoteId: relayRequestId,
      estCheckoutTimeMs:
        (relayQuote.details.timeEstimate || 0) * 1000 +
        RELAY_QUOTE_CHECKOUT_TIME_BUFFER_MS,
      estFeesFromAmount: totalFeesFromAmount.toString(),
      estFeesFromAmountBaseUnit: totalFeesFromAmountBaseUnit.toString(),
      estFeesUsd: totalFeesUsd,
      estMarketMakerGasUsd: relayGasFeesUsd,
      estSubtotalFromAmount,
      estSubtotalFromAmountBaseUnit,
      estSubtotalUsd: toAmountUsd,
      estTotalFromAmount: fromAmountString,
      estTotalFromAmountBaseUnit: fromAmountBaseUnitString,
      estTotalUsd: fromAmountUsd,
      finalToAmountBaseUnit: relayQuote.details.currencyOut.amount ?? '0', // TODO: Or do we want 'minimumAmount'?
      fromTokenAddress: fromTokenAddress as Address, // TODO: fromTokenAddress is typed as `0x${string}` why does not accept SolanaAddress
      lpFeePercentage: 0, // TODO: ?
      lpFeeUsd: totalLpFeesUsd,
      metadata: {
        fromAmountBaseUnit: isExactIn
          ? queryTokenAmountBaseUnit.toString()
          : (relayQuote.details.currencyIn.amount ?? '0'),
        relayQuote: {
          ...relayQuote,
          vmType,
        },
        toAmountBaseUnit: isExactIn
          ? (relayQuote.details.currencyOut.amount ?? '0')
          : queryTokenAmountBaseUnit.toString(),
      },
    }
  } catch (err) {
    if (err instanceof Error && isAPIError(err)) {
      const apiError = err as APIError
      // see the Error section in https://docs.relay.link/references/api/get-quote-v2
      const rawError = apiError.rawError as {
        errorCode?: string
        message?: string
        requestId?: string
      }

      if (rawError?.errorCode) {
        throw new RelayQuoteClientError(
          apiError.statusCode,
          rawError.errorCode,
          rawError.message ?? apiError.message,
          rawError.requestId,
        )
      }
    }

    const message = getErrorMessage(err)
    throw new Error(
      `An error occurred trying to generate a relay quote: ${message}`,
    )
  }
}

function getErrorMessage(err: unknown): string {
  return err instanceof Error
    ? err.message
    : typeof err === 'string'
      ? err
      : JSON.stringify(err)
}
